/* eslint-disable hydrogen/prefer-image-component */
import {ReactElement, useMemo, useCallback, useState, useEffect} from 'react';
import {PlaceholderWhiskyJPEG} from '../../assets/img';

interface FileInputFieldProps {
  name: string;
  value: string | File;
  onChange: (key: string, value: string | File | undefined) => void;
  labelClassName?: string;
  label?: string;
  error?: string;
}

export default function FileInputField({
  name,
  value,
  onChange,
  labelClassName,
  label,
  error,
  ...props
}: FileInputFieldProps): ReactElement {
  const hasError = useMemo(() => !!error, [error]);
  const [imagePreview, setImagePreview] = useState<string>(undefined);

  const handleImageChange = useCallback(
    (event) => {
      const file: File | undefined = event.target.files?.[0];
      setImagePreview(URL.createObjectURL(file));
      onChange(name, file);
    },
    [setImagePreview, onChange, name],
  );

  const resetImage = useCallback(() => {
    setImagePreview(undefined);
    onChange(name, null); // Remove image in the backend by sending null
  }, [setImagePreview, onChange, name]);

  useEffect(() => {
    if (value instanceof File) setImagePreview(URL.createObjectURL(value));
    if (typeof value === 'string' && value.startsWith('http')) {
      setImagePreview(value);
      try {
        fetch(value)
          .then((response) => response.blob())
          .then((blob) => {
            const fileName = value.split('/').pop();
            const file = new File([blob], fileName, {type: blob.type});
            onChange(name, file);
          });
      } catch (error) {
        onChange(name, '');
      }
    }
  }, [name, value, onChange]);

  return (
    <div className={labelClassName}>
      <span className="secondary-font">{label}</span>
      <div className="image-field mt-10">
        <label>
          <div className="image-field__preview">
            <img
              width={300}
              height={300}
              src={imagePreview ?? PlaceholderWhiskyJPEG}
              alt=""
            />
          </div>
          <span className="button button_form button_full mt-15">
            Choose image
          </span>
          <input
            type="file"
            accept="image/*"
            name={name}
            onChange={handleImageChange}
            hidden
            {...props}
          />
        </label>
        {imagePreview && (
          <div className="text-center mt-15">
            <button
              className="text-underline font-13 simpleLink"
              type="button"
              onClick={resetImage}
            >
              Remove Image
            </button>
          </div>
        )}
      </div>
      {hasError && <p className="error-msg mt-10 c-accent">{error}</p>}
    </div>
  );
}

/* eslint-enable hydrogen/prefer-image-component */
